import { useCallback, useEffect, useRef, useState } from "react";
import {
  Folder, FolderPlus, Upload, Search, ChevronRight, Home, MoreHorizontal, Pencil, Trash2, Download, Eye,
  FolderInput, Loader2, Building2, School, User, Lock, HardDrive, X,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useUrlState } from "@/lib/useUrlState";
import { useAuth } from "@/context/AuthContext";
import { canWriteModule } from "@/lib/access";
import { ACCEPT, canPreview, downloadDoc, fileIcon, fmtBytes, fmtDate, fmtSize, openDoc, uploadDoc } from "./docUtils";
import { MoveDialog, NameDialog } from "./DocDialogs";
import { StorageAddonDialog } from "./StorageAddonDialog";

const SCOPE_ICON = { corporate: Building2, school: School, teacher: User };

function StorageMeter({ quota, space, onAdd }) {
  const pct = quota.limit_bytes ? Math.min(100, (quota.used_bytes / quota.limit_bytes) * 100) : 0;
  const tone = pct >= 95 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-primary";
  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600" data-testid="files-usage">
      <div className="flex items-center justify-between gap-2 font-medium text-slate-800">
        <span className="inline-flex items-center gap-1.5"><HardDrive className="h-3.5 w-3.5" /> Storage</span>
        <span className="tabular-nums" data-testid="files-usage-text">{fmtBytes(quota.used_bytes)} / {fmtBytes(quota.limit_bytes)}</span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
        <div className={`h-full rounded-full transition-[width] ${tone}`} style={{ width: `${Math.max(pct, quota.used_bytes ? 1 : 0)}%` }} />
      </div>
      <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-500">
        <span>{pct.toFixed(pct < 10 && pct > 0 ? 1 : 0)}% of this location's limit</span>
        {space && <span>{space.file_count} file{space.file_count === 1 ? "" : "s"} here</span>}
      </div>
      {pct >= 95 && <div className="mt-1.5 text-[11px] font-medium text-red-600" data-testid="files-usage-warning">Almost full — delete files or add storage.</div>}
      {quota.addon_bytes > 0 && <div className="mt-1 text-[11px] text-slate-500" data-testid="files-usage-addon">Includes {fmtBytes(quota.addon_bytes)} purchased</div>}
      {onAdd && <button type="button" onClick={onAdd} className="mt-2 w-full rounded-lg border border-primary/30 bg-primary/5 py-1.5 text-[12px] font-semibold text-primary hover:bg-primary/10" data-testid="files-add-storage-btn">Add storage</button>}
    </div>
  );
}

export function FilesManager({ defaultScope }) {
  const [spaces, setSpaces] = useState(null);
  const [quota, setQuota] = useState(null);
  const [addonOpen, setAddonOpen] = useState(false);
  const { user } = useAuth();
  const isAdmin = ["admin", "corporate_admin"].includes(user?.role);
  const [scope, setScope] = useUrlState("space", defaultScope || "");
  const [folderId, setFolderId] = useUrlState("folder", "");
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [dialog, setDialog] = useState(null); // {type, item}
  const [uploads, setUploads] = useState([]); // [{name, progress, error}]
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef(null);

  const loadSpaces = useCallback(async () => {
    const { data } = await api.get("/docs/spaces");
    const sp = data.spaces;
    setSpaces(sp); setQuota(data.quota);
    if (!sp.some((s) => s.scope === scope)) setScope(sp.find((s) => s.can_write)?.scope || sp[0]?.scope || "");
  }, [scope, setScope]);

  const scopeValid = !!spaces?.some((s) => s.scope === scope);
  const load = useCallback(async () => {
    if (!scope || !scopeValid) return;
    try {
      const { data: d } = await api.get("/docs/browse", { params: { scope, folder_id: folderId || undefined } });
      setData(d);
    } catch (e) {
      if (e.response?.status === 404) { setFolderId(""); return; }
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load files");
    }
  }, [scope, folderId, setFolderId, scopeValid]);

  useEffect(() => { loadSpaces(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {   // back from Stripe: confirm the storage purchase, then refresh the meter
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("storage_session_id");
    if (params.get("storage_canceled")) toast.message("Storage purchase canceled");
    if (!sid) return undefined;
    let tries = 0; let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/docs/storage/checkout/${sid}`);
        if (data.payment_status === "paid") { toast.success(`Storage added — new limit ${fmtBytes(data.quota.limit_bytes)}`); setQuota(data.quota); return; }
        if (data.status === "expired") { toast.error("Checkout session expired"); return; }
      } catch { /* keep polling */ }
      if (!stop && ++tries < 8) setTimeout(tick, 2500);
    };
    tick();
    params.delete("storage_session_id"); params.delete("storage_canceled");
    window.history.replaceState({}, "", `${window.location.pathname}${params.toString() ? `?${params}` : ""}`);
    return () => { stop = true; };
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!q.trim() || !scopeValid) { setResults(null); return undefined; }
    const t = setTimeout(async () => {
      try { const { data: r } = await api.get("/docs/search", { params: { scope, q: q.trim() } }); setResults(r); }
      catch { setResults({ folders: [], files: [] }); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, scope, scopeValid]);

  const space = spaces?.find((s) => s.scope === scope);
  const canWrite = !!data?.can_write && canWriteModule(user, "files");

  const refresh = () => { load(); loadSpaces(); };
  const switchScope = (s) => { setScope(s); setFolderId(""); setQ(""); };
  const openFolder = (id) => { setFolderId(id || ""); setQ(""); };

  const doUpload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const items = list.map((f) => ({ id: `${f.name}-${Date.now()}-${Math.random()}`, name: f.name, progress: 0, error: "" }));
    setUploads((u) => [...u, ...items]);
    let ok = 0;
    for (let i = 0; i < list.length; i++) {
      const id = items[i].id;
      try {
        await uploadDoc({ scope, folderId, file: list[i], onProgress: (p) => setUploads((u) => u.map((x) => (x.id === id ? { ...x, progress: p } : x))) });
        ok++;
        setUploads((u) => u.filter((x) => x.id !== id));
      } catch (e) {
        const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Upload failed";
        setUploads((u) => u.map((x) => (x.id === id ? { ...x, error: msg } : x)));
      }
    }
    if (ok) toast.success(ok === 1 ? "File uploaded" : `${ok} files uploaded`);
    refresh();
  };

  const remove = async (item) => {
    const label = item.kind === "folder" ? `Delete folder “${item.name}” and everything inside it?` : `Delete “${item.name}”?`;
    if (!window.confirm(label)) return;
    try {
      await api.delete(item.kind === "folder" ? `/docs/folders/${item.id}` : `/docs/files/${item.id}`);
      toast.success("Deleted"); refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const onDrop = (e) => { e.preventDefault(); setDragging(false); if (canWrite) doUpload(e.dataTransfer.files); };

  if (!spaces) return <div className="p-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  if (!spaces.length) return <div className="p-10 text-center text-slate-500" data-testid="files-empty">No file libraries available for your account.</div>;

  const folders = results ? results.folders : data?.folders || [];
  const files = results ? results.files : data?.files || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6" data-testid="files-manager">
      <aside className="space-y-1.5 self-start">
        <div className="text-[11px] uppercase tracking-widest text-slate-500 px-2 mb-2">Libraries</div>
        {spaces.map((s) => {
          const Icon = SCOPE_ICON[s.scope] || Folder;
          const active = s.scope === scope;
          return (
            <button key={s.scope} onClick={() => switchScope(s.scope)} data-testid={`files-space-${s.scope}`}
              className={`w-full flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-left transition ${active ? "bg-primary/10 text-primary" : "hover:bg-slate-100 text-slate-700"}`}>
              <Icon className="h-4 w-4 mt-0.5 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium truncate">{s.label}</span>
                <span className="block text-[11px] text-slate-500 truncate">{s.owner_name}</span>
              </span>
              {!s.can_write && <Lock className="h-3.5 w-3.5 text-slate-400 mt-1" title="Read-only" />}
            </button>
          );
        })}
        {quota && <StorageMeter quota={quota} space={space} onAdd={isAdmin ? () => setAddonOpen(true) : null} />}
        <StorageAddonDialog open={addonOpen} onClose={() => setAddonOpen(false)} onApplied={(q) => setQuota(q)} />
      </aside>

      <section className={`rounded-xl border bg-white overflow-hidden min-h-[420px] flex flex-col transition ${dragging ? "border-primary ring-4 ring-primary/15" : "border-slate-200"}`}
        onDragOver={(e) => { e.preventDefault(); if (canWrite) setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}>
        <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
          <nav className="flex items-center gap-1 text-sm min-w-0 flex-1" data-testid="files-breadcrumbs">
            <button onClick={() => openFolder("")} className={`inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-slate-100 ${!folderId ? "font-semibold text-slate-900" : "text-slate-600"}`} data-testid="crumb-root">
              <Home className="h-3.5 w-3.5" /> {space?.label}
            </button>
            {(data?.breadcrumbs || []).map((c, i, arr) => (
              <span key={c.id} className="inline-flex items-center gap-1 min-w-0">
                <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <button onClick={() => openFolder(c.id)} className={`rounded-md px-2 py-1 hover:bg-slate-100 truncate max-w-[180px] ${i === arr.length - 1 ? "font-semibold text-slate-900" : "text-slate-600"}`} data-testid={`crumb-${c.id}`}>{c.name}</button>
              </span>
            ))}
          </nav>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this library…" data-testid="files-search"
              className="h-9 w-56 rounded-lg border border-slate-200 bg-white pl-8 pr-7 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
            {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="h-3.5 w-3.5" /></button>}
          </div>
          {canWrite && (
            <>
              <button onClick={() => setDialog({ type: "newFolder" })} data-testid="files-new-folder-btn"
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 h-9 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <FolderPlus className="h-4 w-4" /> New folder
              </button>
              <button onClick={() => fileInput.current?.click()} data-testid="files-upload-btn"
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 h-9 text-sm font-medium text-white hover:shadow">
                <Upload className="h-4 w-4" /> Upload
              </button>
              <input ref={fileInput} type="file" multiple accept={ACCEPT} className="hidden" data-testid="files-input" onChange={(e) => { doUpload(e.target.files); e.target.value = ""; }} />
            </>
          )}
        </div>

        {uploads.length > 0 && (
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/60 space-y-1.5" data-testid="files-upload-progress">
            {uploads.map((u) => (
              <div key={u.id} className="flex items-center gap-3 text-xs">
                <span className="truncate flex-1 text-slate-700">{u.name}</span>
                {u.error ? (
                  <><span className="text-red-600 truncate max-w-[280px]">{u.error}</span><button onClick={() => setUploads((x) => x.filter((y) => y.id !== u.id))} className="text-slate-400 hover:text-slate-700"><X className="h-3.5 w-3.5" /></button></>
                ) : (
                  <div className="w-40 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${Math.round(u.progress * 100)}%` }} /></div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex-1">
          {results && folders.length === 0 && files.length === 0 && <Empty text={`No matches for “${q}”.`} />}
          {!results && data && folders.length === 0 && files.length === 0 && (
            <Empty text={canWrite ? "This folder is empty. Upload files or create a folder — you can also drag & drop files here." : "This folder is empty."} />
          )}
          {(folders.length > 0 || files.length > 0) && (
            <table className="w-full text-sm" data-testid="files-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium hidden md:table-cell">{results ? "Location" : "Added by"}</th>
                  <th className="px-4 py-2 font-medium hidden sm:table-cell">Modified</th>
                  <th className="px-4 py-2 font-medium text-right">Size</th>
                  <th className="w-12" />
                </tr>
              </thead>
              <tbody>
                {folders.map((f) => (
                  <Row key={f.id} item={{ ...f, kind: "folder" }} canWrite={canWrite} onOpen={() => openFolder(f.id)} onAction={(t) => setDialog({ type: t, item: { ...f, kind: "folder" } })} onDelete={() => remove({ ...f, kind: "folder" })} meta={results ? "Folder" : f.created_by_name} />
                ))}
                {files.map((f) => (
                  <Row key={f.id} item={{ ...f, kind: "file" }} canWrite={canWrite} onOpen={() => (canPreview(f.ext) ? openDoc(f) : downloadDoc(f))} onAction={(t) => setDialog({ type: t, item: { ...f, kind: "file" } })} onDelete={() => remove({ ...f, kind: "file" })} meta={results ? (f.folder_name || "Top level") : f.uploaded_by_name} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <NameDialog open={dialog?.type === "newFolder"} onClose={() => setDialog(null)} title="New folder" confirmLabel="Create" testid="new-folder-dialog"
        onSubmit={async (name) => { await api.post("/docs/folders", { scope, parent_id: folderId || null, name }); refresh(); }} />
      <NameDialog open={dialog?.type === "rename"} onClose={() => setDialog(null)} title={`Rename ${dialog?.item?.kind === "folder" ? "folder" : "file"}`} initial={dialog?.item?.name || ""} confirmLabel="Rename" testid="rename-dialog"
        onSubmit={async (name) => { await api.patch(dialog.item.kind === "folder" ? `/docs/folders/${dialog.item.id}` : `/docs/files/${dialog.item.id}`, { name }); refresh(); }} />
      <MoveDialog open={dialog?.type === "move"} onClose={() => setDialog(null)} scope={scope} item={dialog?.item}
        onMove={async (dest) => { await api.patch(dialog.item.kind === "folder" ? `/docs/folders/${dialog.item.id}` : `/docs/files/${dialog.item.id}`, { move: true, parent_id: dest }); toast.success("Moved"); refresh(); }} />
    </div>
  );
}

function Empty({ text }) {
  return <div className="px-6 py-16 text-center text-sm text-slate-500" data-testid="files-empty-state">{text}</div>;
}

function Row({ item, canWrite, onOpen, onAction, onDelete, meta }) {
  const isFolder = item.kind === "folder";
  const { Icon, cls } = isFolder ? { Icon: Folder, cls: "text-amber-500 bg-amber-50" } : fileIcon(item.ext);
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/70 group" data-testid={`${item.kind}-row-${item.id}`}>
      <td className="px-4 py-2.5">
        <button onClick={onOpen} className="flex items-center gap-3 text-left min-w-0 max-w-full" data-testid={`${item.kind}-open-${item.id}`}>
          <span className={`h-8 w-8 rounded-lg inline-flex items-center justify-center shrink-0 ${cls}`}><Icon className="h-4 w-4" /></span>
          <span className="truncate font-medium text-slate-900 group-hover:text-primary">{item.name}</span>
        </button>
      </td>
      <td className="px-4 py-2.5 text-slate-600 hidden md:table-cell truncate max-w-[200px]">{meta}</td>
      <td className="px-4 py-2.5 text-slate-500 hidden sm:table-cell whitespace-nowrap">{fmtDate(item.updated_at || item.created_at)}</td>
      <td className="px-4 py-2.5 text-slate-600 text-right whitespace-nowrap">{isFolder ? "—" : fmtSize(item.size)}</td>
      <td className="px-2 py-2.5 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="h-8 w-8 inline-flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-200/70" data-testid={`${item.kind}-menu-${item.id}`}><MoreHorizontal className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            {!isFolder && canPreview(item.ext) && <DropdownMenuItem onClick={() => openDoc(item)} data-testid={`file-preview-${item.id}`}><Eye className="h-4 w-4 mr-2" /> Preview</DropdownMenuItem>}
            {!isFolder && <DropdownMenuItem onClick={() => downloadDoc(item)} data-testid={`file-download-${item.id}`}><Download className="h-4 w-4 mr-2" /> Download</DropdownMenuItem>}
            {isFolder && <DropdownMenuItem onClick={onOpen}><Folder className="h-4 w-4 mr-2" /> Open</DropdownMenuItem>}
            {canWrite && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => onAction("rename")} data-testid={`${item.kind}-rename-${item.id}`}><Pencil className="h-4 w-4 mr-2" /> Rename</DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAction("move")} data-testid={`${item.kind}-move-${item.id}`}><FolderInput className="h-4 w-4 mr-2" /> Move to…</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-700" data-testid={`${item.kind}-delete-${item.id}`}><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  );
}
